import { Close, East, West } from '@mui/icons-material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { splitOrder } from '../../../api/order/splitOrder';
import { Cafe, Dish, Order, OrderItem } from '../../../apiTypes';
import Image from '../../../components/Image';
import PrimaryButton from '../../../components/PrimaryButton';
import { t } from '../../../constants/translations';
import { useLocale } from '../../../contexts/locale.context';
import currency from '../../../utils/currency';
import { getOrderItemPriceWithMods } from '../../../utils/getBillCalc';
import { Map } from '../../../utils/map';
import useMedia from '../../../utils/useMedia';

type ItemProps = {
  items: OrderItem[];
  onClick: (item: OrderItem) => void;
  isFirstOrder?: boolean;
  dishesMap: Map<DbDocument<Dish>>;
  cafe: DbDocument<Cafe> | null | undefined;
};

const List = ({ items, isFirstOrder, dishesMap, onClick, cafe }: ItemProps) => {
  const { isMobile } = useMedia();
  const { lang } = useLocale();

  const total = useMemo(() => items.reduce<number>((memo, item) => getOrderItemPriceWithMods(item) + memo, 0), [items]);

  return (
    <Box
      sx={{
        width: { xs: 300, sm: 350, md: 400, lg: 500 },
        flexShrink: 0,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: (t) => t.palette.grey[300],
        borderRadius: 1,
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{ position: 'sticky', top: 0, bgcolor: (t) => t.palette.background.paper, zIndex: (t) => t.zIndex.tooltip }}
      >
        <Typography sx={{ p: 2 }} variant="h6" textAlign="center">{`${t.order[lang]} ${
          isFirstOrder ? '1' : '2'
        }`}</Typography>
        <Divider sx={{ m: 0, p: 0 }} />
      </Box>

      <Box
        sx={{ minHeight: window.innerHeight - (isMobile ? 304 : 368) }}
        data-testid={`order-item-list-${isFirstOrder ? '1' : '2'}`}
      >
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              m: 1,
            }}
            data-testid="order-item"
            onClick={() => onClick(item)}
          >
            <Box sx={{ mr: 1, height: 40 }}>
              <Image src={dishesMap[item.dishId]?.imageUrl} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ lineHeight: 1.1, mb: 0.5 }}>{dishesMap[item.dishId]?.name}</Typography>

              {item.variation && (
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1 }}
                  color={(t) => (item.status === 'complete' ? t.palette.text.disabled : t.palette.text.secondary)}
                >
                  {item.variation.name}
                </Typography>
              )}
              {item.modifications?.map((mod) => (
                <Typography
                  key={mod.name}
                  variant="body2"
                  sx={{ lineHeight: 1 }}
                  color={(t) => t.palette.text.secondary}
                >
                  {mod.name}
                </Typography>
              ))}
            </Box>

            <Typography sx={{ ml: 1, color: (t) => t.palette.text.secondary }}>
              {currency(getOrderItemPriceWithMods(item), cafe?.currency, true)}
            </Typography>

            <IconButton>{isFirstOrder ? <East /> : <West />}</IconButton>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: (t) => t.palette.background.paper,
          zIndex: (t) => t.zIndex.tooltip,
        }}
        data-testid={`order-total-${isFirstOrder ? '1' : '2'}`}
      >
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">{`${t.total[lang]}:`}</Typography>
          <Typography variant="h6" color={(t) => t.palette.text.secondary}>
            {currency(Number(total), cafe?.currency)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

type Props = {
  onClose: () => void;
  order: DbDocument<Order>;
  workerId: string;
  workerName: string;
  cafe: DbDocument<Cafe> | null | undefined;
  dishesMap: Map<DbDocument<Dish>>;
};

const SplitOrderModal = ({ order, onClose, workerId, workerName, cafe, dishesMap }: Props) => {
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const [itemsIds, setItemIds] = useState<string[]>([]);

  const firstOrderItems = useMemo(
    () => Object.values(order.items).filter((item) => !itemsIds.includes(item.id)),
    [itemsIds, order.items]
  );

  const secondOrderItems = useMemo(
    () => Object.values(order.items).filter((item) => itemsIds.includes(item.id)),
    [itemsIds, order.items]
  );

  const handleFirstOrderItemClick = useCallback((item: OrderItem) => {
    setItemIds((prev) => [...prev, item.id]);
  }, []);

  const handleSecondOrderItemClick = useCallback((item: OrderItem) => {
    setItemIds((prev) => prev.filter((i) => i !== item.id));
  }, []);

  const handleSave = useCallback(async () => {
    if (itemsIds.length > 0) {
      const newItems = Object.fromEntries(Object.entries(order.items).filter(([key]) => itemsIds.includes(key)));
      const oldItems = Object.fromEntries(Object.entries(order.items).filter(([key]) => !itemsIds.includes(key)));

      if (workerName && cafe) {
        await splitOrder({
          cafeSlug: cafe.id,
          order,
          workerId,
          workerName,
          cafe,
          newItems,
          oldItems,
        });
      }

      onClose();
    }
  }, [cafe, itemsIds, onClose, order, workerId, workerName]);

  return (
    <Dialog maxWidth="lg" open onClose={onClose} fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {t.split_order[lang]}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 2, overflow: 'auto' }}>
        <List
          dishesMap={dishesMap}
          cafe={cafe}
          items={firstOrderItems}
          onClick={handleFirstOrderItemClick}
          isFirstOrder
        />
        <List dishesMap={dishesMap} cafe={cafe} items={secondOrderItems} onClick={handleSecondOrderItemClick} />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <PrimaryButton variant="contained" onClick={handleSave} disabled={itemsIds.length === 0}>
          {t.save_changes[lang]}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SplitOrderModal;
